import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, tap, shareReplay, finalize, switchMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

/**
 * Subscription info with dynamic headers array
 */
interface SubscriptionInfo {
  appName: string;
  headers: Array<{ [key: string]: string }>;
}

/**
 * OAuth2 token response from API
 */
interface OAuth2TokenResponse {
  environment: string;
  accessToken: string;
  tokenType: string;
  state: string;
  cached: boolean;
  hasRefreshToken: boolean;
  expiresIn?: number;
  expiresAt: string;
  timestamp: string;
  subscriptions: SubscriptionInfo[];
}

/**
 * Cached token structure
 */
interface CachedToken {
  access_token: string;
  expiresAt: Date;
  timestamp: Date;
  subscriptions: SubscriptionInfo[];
}

/**
 * Token data for caching (pre-serialization)
 */
interface TokenData {
  access_token: string;
  expiresAt: string;
  timestamp: string;
  subscriptions: SubscriptionInfo[];
}

@Injectable({
  providedIn: 'root'
})
export class OAuth2TokenService {
  private tokenFetchInProgress = new Map<string, Observable<any>>();

  constructor(private http: HttpClient) {}

  /**
   * Get or fetch OAuth2 token with automatic caching and refresh
   * @param endpoint OAuth2 endpoint URL
   * @param tokenVariableName OAuth2 token variable name (e.g., "OAUTH2_ROYALSUNDARAM")
   * @returns Observable<string> - The access token
   */
  getOrFetchToken(endpoint: string, tokenVariableName: string): Observable<any> {
    // const envName = environment.selectedEnv as keyof typeof environment;
    const envName = "UAT";
    const storageKey = `${tokenVariableName}_${envName}`;
    
    // Check if valid cached token exists
    const cachedToken = this.getCachedToken(storageKey);
    if (cachedToken && this.isTokenValid(cachedToken)) {
      return of(cachedToken);
    }

    // Check if token fetch is already in progress
    const inProgressFetch = this.tokenFetchInProgress.get(storageKey);
    if (inProgressFetch) {
      return inProgressFetch;
    }

    // Create shared observable for concurrent requests
    const tokenRequest$ = this.fetchTokenFromEndpoint(endpoint, envName as string).pipe(
      tap(tokenResponse => this.cacheToken(storageKey, tokenResponse)),
      map(() => this.getCachedToken(storageKey)),
      shareReplay(1),
      finalize(() => this.tokenFetchInProgress.delete(storageKey))
    );

    this.tokenFetchInProgress.set(storageKey, tokenRequest$);
    return tokenRequest$;
  }

  /**
   * Get all headers for a specific app from cached token
   * @param appName Application name 
   * @param tokenVariableName OAuth2 token variable name (e.g., "OAUTH2_ROYALSUNDARAM")
   * @returns Object with all headers, or null if not found
   */
  getHeadersForApp(appName: string, tokenVariableName: string): { [key: string]: string } | null {
    // const envName = environment.selectedEnv as keyof typeof environment;
    const envName = "UAT";
    const storageKey = `${tokenVariableName}_${envName}`;

    const cachedToken = this.getCachedToken(storageKey);
    
    if (!cachedToken || !cachedToken.subscriptions) {
      return null;
    }

    // Find subscription for the given appName
    const subscription = cachedToken.subscriptions.find(sub => sub.appName === appName);
    
    if (!subscription || !subscription.headers || subscription.headers.length === 0) {
      return null;
    }

    // Merge all header objects into a single object
    const mergedHeaders: { [key: string]: string } = {};
    
    subscription.headers.forEach(headerObj => {
      Object.keys(headerObj).forEach(key => {
        mergedHeaders[key] = headerObj[key];
      });
    });

    return Object.keys(mergedHeaders).length > 0 ? mergedHeaders : null;
  }

  /**
   * Fetch token from OAuth2 endpoint using POST method
   * @param endpoint OAuth2 endpoint URL
   * @param environment Environment name 
   * @returns Observable with normalized token data
   */
  private fetchTokenFromEndpoint(endpoint: string, environment: string): Observable<TokenData> {
    const payload = { environment };
    
    return this.http.post<OAuth2TokenResponse>(endpoint, payload).pipe(
      map(response => ({
        access_token: response.accessToken,
        expiresAt: response.expiresAt,
        timestamp: response.timestamp,
        subscriptions: response.subscriptions
      } as TokenData))
    );
  }

  /**
   * Get cached token from session storage
   * @param storageKey Storage key for the environment
   * @returns CachedToken | null
   */
  private getCachedToken(storageKey: string): CachedToken | null {
    try {
      const cached = sessionStorage.getItem(storageKey);
      if (!cached) {
        return null;
      }

      const parsed = JSON.parse(cached);
      
      return {
        access_token: parsed.access_token,
        expiresAt: new Date(parsed.expiresAt),
        timestamp: new Date(parsed.timestamp),
        subscriptions: parsed.subscriptions || []
      };
    } catch (e) {
      this.logError('Failed to parse cached token:', e);
      return null;
    }
  }

  /**
   * Cache token in session storage
   * @param storageKey Storage key for the environment
   * @param tokenResponse OAuth2 token response
   * @param retryCount Number of retry attempts (internal use)
   */
  private cacheToken(storageKey: string, tokenResponse: TokenData, retryCount: number = 0): void {
    try {
      const tokenData: CachedToken = {
        access_token: tokenResponse.access_token,
        expiresAt: new Date(tokenResponse.expiresAt),
        timestamp: new Date(tokenResponse.timestamp),
        subscriptions: tokenResponse.subscriptions || []
      };
      
      const serialized = JSON.stringify(tokenData);
      
      // Warn if data is large
      if (serialized.length > 1024 * 1024) { // 1MB
        this.logWarn(`Token cache is large: ${(serialized.length / 1024).toFixed(2)}KB for ${storageKey}`);
      }
      
      sessionStorage.setItem(storageKey, serialized);
      
    } catch (e) {
      // Handle quota exceeded error with retry
      if (e instanceof Error && e.name === 'QuotaExceededError' && retryCount < 1) {
        this.logError('SessionStorage quota exceeded. Clearing old tokens...');
        this.clearOldestToken();
        this.cacheToken(storageKey, tokenResponse, retryCount + 1); // Recursive retry
      } else {
        this.logError('Failed to cache OAuth2 token:', e);
      }
    }
  }

  /**
   * Clear oldest token from session storage to free up space
   * Finds token with earliest timestamp and removes it
   * Used when QuotaExceededError occurs during token caching
   * @private
   */
  private clearOldestToken(): void {
    const keys = Object.keys(sessionStorage);
    
    // Filter for valid token keys by checking if value is a parseable token object
    const tokenKeys = keys.filter(key => {
      try {
        const cached = sessionStorage.getItem(key);
        if (!cached) return false;
        
        const parsed = JSON.parse(cached);
        // Check if it looks like a token object (has required fields)
        return parsed.access_token && parsed.expiresAt && parsed.timestamp;
      } catch {
        return false;
      }
    });
    
    if (tokenKeys.length === 0) {
      return;
    }

    // Find the token with the oldest timestamp
    let oldestKey: string | null = null;
    let oldestTimestamp: number = Date.now();

    tokenKeys.forEach(key => {
      try {
        const cached = sessionStorage.getItem(key);
        if (cached) {
          const parsed = JSON.parse(cached);
          const timestamp = new Date(parsed.timestamp).getTime();
          
          if (!isNaN(timestamp) && timestamp < oldestTimestamp) {
            oldestTimestamp = timestamp;
            oldestKey = key;
          }
        }
      } catch (e) {
        // If parsing fails, this key is a good candidate for removal
        if (!oldestKey) {
          oldestKey = key;
        }
      }
    });

    if (oldestKey) {
      sessionStorage.removeItem(oldestKey);
      this.logInfo(`Cleared oldest token: ${oldestKey} (timestamp: ${new Date(oldestTimestamp).toISOString()})`);
    }
  }

  /**
   * Log error message (disabled in production)
   */
  private logError(message: string, ...args: any[]): void {
    if (!environment.production) {
      console.error(`[OAuth2Service] ${message}`, ...args);
    }
  }

  /**
   * Log warning message (disabled in production)
   */
  private logWarn(message: string, ...args: any[]): void {
    if (!environment.production) {
      console.warn(`[OAuth2Service] ${message}`, ...args);
    }
  }

  /**
   * Log info message (disabled in production)
   */
  private logInfo(message: string, ...args: any[]): void {
    if (!environment.production) {
      console.log(`[OAuth2Service] ${message}`, ...args);
    }
  }

  /**
   * Check if token is valid (expires in more than 30 seconds)
   * @param token Cached token object
   * @returns boolean
   */
  private isTokenValid(token: CachedToken): boolean {
    try {
      const now = new Date();
      const expiresAt = new Date(token.expiresAt);
      
      // Validate dates
      if (isNaN(expiresAt.getTime())) {
        this.logError('Invalid expiration date in cached token');
        return false;
      }
      
      const bufferTime = 30 * 1000; // 30 seconds in milliseconds
      const remainingTime = expiresAt.getTime() - now.getTime();
      
      return remainingTime > bufferTime;
    } catch (e) {
      this.logError('Error validating token expiry:', e);
      return false;
    }
  }

  /**
   * Clear cached token (for logout or manual refresh)
   * @param tokenVariableName Variable name (e.g., 'OAUTH2_ROYALSUNDARAM', 'GOOGLE_ENDPOINT')
   * @param envName Environment name
   */
  clearToken(tokenVariableName: string, envName?: string): void {
    // const env = envName || (environment.selectedEnv as string);
    const env = envName || "UAT"; 
    const storageKey = `${tokenVariableName}_${env}`;
    sessionStorage.removeItem(storageKey);
  }
}